import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { meanLumaInRect } from '../image/analysis.js';
import { missingRequirementReason, notChecked, plainResult, withRequirement } from './support.js';

/**
 * Eye-area obstruction check (glasses frames, tinted lenses, glare).
 *
 * No landmark model tells us whether a person wears glasses, so this stays a
 * *detector of optical symptoms*, not a classifier. The wording and the status
 * reflect that:
 *
 *   strong symptoms (rim + glare) → warning, confidence ~0.55
 *   one symptom only             → uncertain, confidence ~0.35
 *   no symptoms                  → pass, with an explicit "эвристика" note
 *
 * The check never states "на фото очки".
 */
export const glassesCheck: ValidationCheck = {
  id: 'glasses',
  title: 'Область глаз: оправа и блики',
  description: 'Ищет оптические признаки препятствий в области глаз (эвристика по яркости).',
  kind: 'content',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, glassesCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    const pixels = ctx.pixels;
    if (!geometry || !pixels) {
      return notChecked(ctx, glassesCheck, 'Нет данных для анализа области глаз.', ctx.rule.severity);
    }

    const maxGlareRatio = numberParam(ctx.rule.params, 'maxGlareRatio', 0.035);
    const maxRimRun = numberParam(ctx.rule.params, 'maxRimRun', 0.35);

    const headBox = geometry.headBox;
    const eyeBand = {
      x: clamp(headBox.x, 0, 1),
      y: clamp(geometry.eyeLineFromTop - 0.055, 0, 1),
      width: clamp(headBox.width, 0.02, 1),
      height: clamp(0.11, 0.01, 1),
    };
    const faceBox = geometry.faceBox;

    const { luma, width, height } = pixels;
    // Narrow the band to the central 70 % of the head: the corners of a bounding
    // box are background, and a white wall is not a glare on a lens.
    const measuredBand = {
      x: eyeBand.x + eyeBand.width * 0.15,
      width: eyeBand.width * 0.7,
      y: eyeBand.y,
      height: eyeBand.height,
    };
    const x0 = Math.max(0, Math.floor(measuredBand.x * width));
    const x1 = Math.min(width, Math.ceil((measuredBand.x + measuredBand.width) * width));
    const y0 = Math.max(0, Math.floor(eyeBand.y * height));
    const y1 = Math.min(height, Math.ceil((eyeBand.y + eyeBand.height) * height));

    if (x1 - x0 < 8 || y1 - y0 < 4) {
      return notChecked(
        ctx,
        glassesCheck,
        'Область глаз слишком мала для анализа бликов.',
        ctx.rule.severity,
      );
    }

    let bright = 0;
    let total = 0;
    let longestDarkRun = 0;
    const faceMean = Math.max(1, meanLumaInRect(luma, width, height, faceBox));
    const darkThreshold = faceMean * 0.62;

    for (let y = y0; y < y1; y += 1) {
      let run = 0;
      for (let x = x0; x < x1; x += 1) {
        const value = luma[y * width + x] as number;
        total += 1;
        if (value >= 246) bright += 1;
        if (value < darkThreshold) {
          run += 1;
          if (run > longestDarkRun) longestDarkRun = run;
        } else {
          run = 0;
        }
      }
    }

    const glareRatio = total > 0 ? bright / total : 0;
    const rimRun = x1 - x0 > 0 ? longestDarkRun / (x1 - x0) : 0;

    const glareSuspicious = glareRatio > maxGlareRatio;
    const rimSuspicious = rimRun > maxRimRun;
    const symptoms = [rimSuspicious, glareSuspicious].filter(Boolean).length;

    const status = symptoms === 2 ? 'warning' : symptoms === 1 ? 'uncertain' : 'pass';
    const confidence = symptoms === 2 ? 0.55 : symptoms === 1 ? 0.35 : 0.5;

    const found: string[] = [];
    if (rimSuspicious) found.push('тёмная горизонтальная полоса на уровне глаз (похоже на оправу)');
    if (glareSuspicious) found.push('яркие блики на уровне глаз (похоже на отражение в стёклах)');

    const message = (() => {
      if (status === 'warning') {
        return `⚠ Возможны препятствия в области глаз: ${found.join('; ')}. Требуется визуальная проверка.`;
      }
      if (status === 'uncertain') {
        return `Не удалось уверенно определить, есть ли препятствия в области глаз: ${found.join('; ')}.`;
      }
      return 'Явных признаков оправы и бликов на уровне глаз не найдено (проверка по яркости).';
    })();


    const result = plainResult({
      id: glassesCheck.id,
      title: glassesCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(
        Math.max(glareRatio / Math.max(maxGlareRatio, 1e-6), rimRun / Math.max(maxRimRun, 1e-6)),
        2,
      ),
      message,
      requirement: ctx.rule.official?.statement,
      fixability: 'retake',
      geometry: [
        {
          type: 'rect',
          coordinates: [
            roundTo(eyeBand.x, 4),
            roundTo(eyeBand.y, 4),
            roundTo(eyeBand.width, 4),
            roundTo(eyeBand.height, 4),
          ],
        },
      ],
      problemRegions:
        status === 'pass'
          ? []
          : [
              {
                id: 'eye_area_obstruction',
                rect: {
                  x: roundTo(eyeBand.x, 4),
                  y: roundTo(eyeBand.y, 4),
                  width: roundTo(eyeBand.width, 4),
                  height: roundTo(eyeBand.height, 4),
                },
                message:
                  status === 'warning'
                    ? 'Область глаз: возможны оправа или блик.'
                    : 'Область глаз: проверьте вручную.',
                severity: 'warning',
              },
            ],
      confidence,
      details: {
        glareRatio: roundTo(glareRatio, 4),
        longestDarkRunRatio: roundTo(rimRun, 4),
        maxGlareRatio,
        maxRimRun,
        symptoms,
        classifier: 'heuristic-brightness',
        note:
          'Эвристика по яркости: проверка указывает на возможную проблему, а не утверждает её. Классификатора очков в модели нет.',
        displayValue: status === 'pass' ? 'не найдено' : 'возможны препятствия',
      },
    });

    return withRequirement(result, ctx.rule, { unit: 'ratio' });
  },
};
